import { useEffect, useRef, useState } from 'react'
import { LayoutGrid, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { TooltipProvider } from '@/components/ui/tooltip'
import { EnvironmentBadge } from '@/components/layout/EnvironmentBadge'
import { MainMenu } from '@/components/layout/MainMenu'
import { dashboardNavGroups } from '@/lib/dashboardNav'
import { iconForDashboardNav } from '@/lib/dashboardNavIcons'
import { navFeatureFor } from '@/lib/featureFlags'
import {
  HOME_TILES,
  canAddHomeLayout,
  defaultTileOrder,
  homeTile,
  isTileAllowed,
  mergeHiddenTiles,
} from '@/lib/homeLayouts'
import { showsLisQueue } from '@/lib/lisReview'
import { navBadgeCount } from '@/lib/notificationNavBadges'
import { PERMISSIONS, navPermissionsFor, usePermission } from '@/lib/permissions'
import { cn } from '@/lib/utils'
import { useHomeLayouts } from '@/hooks/useHomeLayouts'
import { useLongPress } from '@/hooks/useLongPress'
import { useUnreadCount } from '@/hooks/useNotifications'
import { useFeatureFlags } from '@/providers/FeatureFlagsProvider'
import { ChooseAppsDialog } from './ChooseAppsDialog'
import { HomeEditGrid } from './HomeEditGrid'
import { HomeLayoutMenu } from './HomeLayoutMenu'
import { LisQueueTileBadge } from '@/components/lis-review/LisQueueTileBadge'
import { HomeTileCard, type HomeNavItem } from './HomeTile'
import { NameLayoutDialog } from './NameLayoutDialog'

/** `layoutId: null` is a new layout, named on Done. */
type EditState = { layoutId: string | null; draft: string[] }

const SECTION_TITLE = 'pt-1 mb-2 text-center text-[11px] font-bold leading-tight underline'

/**
 * The Home tile panel (z8nrz7ep6x): the default three columns, or the user's
 * active layout as one "My Home" grid, plus edit mode.
 */
export function HomeNavPanel() {
  const { can, canAny, isSuperAdmin } = usePermission()
  const { flags, isLoading: flagsLoading } = useFeatureFlags()
  // One query for the whole nav; the socket invalidates the shared notification
  // key prefix, so every badge picks up realtime pushes for free.
  const { data: unreadCounts } = useUnreadCount()
  const layouts = useHomeLayouts()

  const [edit, setEdit] = useState<EditState | null>(null)
  const [chooseOpen, setChooseOpen] = useState(false)
  const [nameOpen, setNameOpen] = useState(false)

  // Fails OPEN, unlike the route guard: while the map loads, or for a key the
  // backend has not shipped, the card stays. A card that briefly shows and then
  // vanishes is a smaller sin than a nav that renders empty on every cold load,
  // and clicking through early just hits FeatureGated, which decides properly.
  const featureOn = (to: string) => {
    if (flagsLoading) return true
    return navFeatureFor(to).every((feature) => flags[feature] !== false)
  }

  // Permission HIDES, a feature flag only GREYS — see HomeTileCard for why the
  // two gates get different treatment.
  const access = { isSuperAdmin, canAnyRoute: (to: string) => canAny(navPermissionsFor(to)) }
  const allowedId = (id: string) => {
    const tile = homeTile(id)
    return !!tile && isTileAllowed(tile, access)
  }
  const allowedTiles = HOME_TILES.filter((tile) => isTileAllowed(tile, access))

  const toItem = (id: string): HomeNavItem => {
    const tile = homeTile(id)!
    return { ...tile, Icon: iconForDashboardNav(tile.label), disabled: !featureOn(tile.to) }
  }
  const itemsFor = (ids: string[]) => ids.filter(allowedId).map(toItem)

  const active = layouts.active

  // Customizing needs the stored layouts first: every save replaces the whole
  // value, so saving before they load (or after the read failed) would wipe
  // them. At the cap a new layout could not be saved, so editing would only
  // throw the work away on Done.
  const canCustomize = layouts.ready
  const canCreate = canCustomize && canAddHomeLayout(layouts.prefs)

  const startNew = () => {
    if (canCreate) setEdit({ layoutId: null, draft: defaultTileOrder(allowedId) })
  }
  const editLayout = (id: string) => {
    const layout = layouts.prefs.layouts.find((l) => l.id === id)
    if (!canCustomize || !layout) return
    // Edited from the menu, a layout that is not showing becomes the one that
    // is, so Cancel leaves Home on the layout the user was working on.
    if (layout.id !== active?.id) layouts.select(layout.id)
    setEdit({ layoutId: layout.id, draft: layout.tiles.filter(allowedId) })
  }
  const startEdit = () => {
    if (active) editLayout(active.id)
    else startNew()
  }
  const setDraft = (draft: string[]) => setEdit((e) => (e ? { ...e, draft } : e))

  const finish = () => {
    if (!edit) return
    if (edit.layoutId === null) {
      setNameOpen(true)
      return
    }
    const previous = layouts.prefs.layouts.find((l) => l.id === edit.layoutId)?.tiles ?? []
    layouts.saveTiles(edit.layoutId, mergeHiddenTiles(edit.draft, previous, allowedId))
    setEdit(null)
  }

  const press = useLongPress(() => {
    if (!edit) startEdit()
  })

  // The control that opened or closed the editor unmounts with the switch, so
  // move keyboard focus by hand: into the toolbar on entry, back to the
  // pencil on exit. Skipped on first render, which is not a switch.
  const editing = edit !== null
  const menuRef = useRef<HTMLDivElement>(null)
  const toolbarRef = useRef<HTMLDivElement>(null)
  const firstRender = useRef(true)
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false
      return
    }
    const target = editing ? toolbarRef.current : menuRef.current
    target?.querySelector('button')?.focus()
  }, [editing])

  const badge = (item: HomeNavItem) => navBadgeCount(item.to, unreadCounts)
  // The LIS queue is for the people who approve, so only they see its count.
  const canReviewLis = can(PERMISSIONS.submissionsEdit)
  const corner = (item: HomeNavItem) =>
    canReviewLis && showsLisQueue(item.to) ? <LisQueueTileBadge label={item.label} /> : undefined

  return (
    <aside className='flex w-80 shrink-0 flex-col overflow-hidden border-r border-gray-200 bg-white'>
      {/* TopBar is hidden on this route, so the brand lives here. */}
      <div className='relative flex shrink-0 items-center justify-center gap-2 px-3 pb-1 pt-2'>
        {/* The main menu (z8nrz7f7g1) left of the brand, as on every other page's
            top bar. Hidden while editing: leaving would drop the draft. */}
        {!edit && (
          <div className='absolute left-2 top-1/2 -translate-y-1/2'>
            <MainMenu />
          </div>
        )}
        {!edit && canCustomize && (
          <div ref={menuRef} className='absolute right-3 top-1/2 -translate-y-1/2'>
            <HomeLayoutMenu
              prefs={layouts.prefs}
              onSelect={layouts.select}
              onCreate={startNew}
              onEdit={editLayout}
              onDelete={layouts.remove}
            />
          </div>
        )}
        <img
          src='/images/title21-lockup.png'
          alt='Title21'
          width={1322}
          height={336}
          // w-40, not wider: the centred lockup plus the STAGING badge must
          // clear the ☰ and pencil pinned to either edge of this 320px panel.
          className='h-auto w-40 shrink-0'
        />
        {/* Renders nothing on production and on every client deployment, so
            the lockup stays centred there. */}
        <EnvironmentBadge />
      </div>

      {edit && (
        <div
          ref={toolbarRef}
          className='flex shrink-0 flex-wrap items-center gap-1.5 border-y border-blue-100 bg-blue-50/60 px-3 py-2'
        >
          <Button size='sm' variant='outline' className='h-7 px-2 text-xs' onClick={() => setChooseOpen(true)}>
            <LayoutGrid className='mr-1 h-3.5 w-3.5' />
            Choose apps
          </Button>
          <Button
            size='sm'
            variant='ghost'
            className='h-7 px-2 text-xs'
            title='Show every app in the default order'
            onClick={() => setDraft(defaultTileOrder(allowedId))}
          >
            <RotateCcw className='mr-1 h-3.5 w-3.5' />
            Reset
          </Button>
          <div className='ml-auto flex gap-1.5'>
            <Button size='sm' variant='ghost' className='h-7 px-2 text-xs' onClick={() => setEdit(null)}>
              Cancel
            </Button>
            <Button
              size='sm'
              className='h-7 px-3 text-xs'
              disabled={edit.draft.length === 0}
              title={edit.draft.length === 0 ? 'Add at least one app' : undefined}
              onClick={finish}
            >
              Done
            </Button>
          </div>
        </div>
      )}

      {/* Only the menu scrolls; the brand header above stays put. */}
      <div className='scrollbar-none min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-3 pb-4'>
        <TooltipProvider delayDuration={200}>
          {edit ? (
            <div className='pt-3'>
              <p className='mb-2 text-center text-[11px] text-gray-500'>
                Drag to move. Tap "−" to hide.
              </p>
              {edit.draft.length === 0 ? (
                <p className='py-8 text-center text-sm text-gray-400'>
                  No apps on this layout. Use Choose apps to add some.
                </p>
              ) : (
                <HomeEditGrid items={edit.draft.map(toItem)} draft={edit.draft} onChange={setDraft} />
              )}
            </div>
          ) : layouts.isLoading ? null : active ? (
            <div className='flex flex-col items-center p-1.5'>
              <p className={cn(SECTION_TITLE, 'mb-0 text-blue-700')}>My Home</p>
              <p className='mb-2 text-[10px] text-gray-400'>{active.name}</p>
              {itemsFor(active.tiles).length === 0 ? (
                <p className='py-8 text-center text-sm text-gray-400'>No apps on this layout.</p>
              ) : (
                <div className='grid w-full grid-cols-3 gap-1.5'>
                  {itemsFor(active.tiles).map((item) => (
                    <HomeTileCard key={item.id} item={item} badge={badge(item)} press={press} corner={corner(item)} />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className='flex gap-2'>
              {dashboardNavGroups
                .map((group) => ({ ...group, items: itemsFor(group.items.map((i) => i.id)) }))
                // A group whose every card is permission-denied drops; one
                // whose cards are all feature-off renders greyed.
                .filter((group) => group.items.length > 0)
                .map((group) => (
                  <div
                    key={group.title}
                    className='flex min-w-0 flex-1 flex-col items-center gap-1.5 rounded-xl p-1.5'
                  >
                    <p className={cn(SECTION_TITLE, group.text)}>{group.title}</p>
                    {group.items.map((item) => (
                      <HomeTileCard key={item.id} item={item} badge={badge(item)} press={press} corner={corner(item)} />
                    ))}
                  </div>
                ))}
            </div>
          )}
        </TooltipProvider>
      </div>

      {edit && (
        <ChooseAppsDialog
          open={chooseOpen}
          onOpenChange={setChooseOpen}
          draft={edit.draft}
          allowed={allowedTiles}
          onChange={setDraft}
        />
      )}
      <NameLayoutDialog
        open={nameOpen}
        onOpenChange={setNameOpen}
        prefs={layouts.prefs}
        onSave={(name) => {
          if (edit) layouts.create(name, edit.draft)
          setEdit(null)
        }}
      />
    </aside>
  )
}
